import { useQuery } from "@tanstack/react-query";

import type { DocumentWorkflowDocument } from "../entities/document-workflow";
import { MailAttachments } from "./MailAttachments";
import { priorityLabels } from "./documentWorkflowSearch";
import type { ShellServices } from "./services";

const typeLabels: Record<DocumentWorkflowDocument["type"], string> = {
  incoming: "Входящий",
  outgoing: "Исходящий",
  internal: "Внутренний",
};

function formatDate(value: string | null | undefined, withTime = false) {
  return !value || Number.isNaN(Date.parse(value))
    ? null
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        ...(withTime ? { timeStyle: "short" as const } : {}),
      }).format(new Date(value));
}

function sender(
  value: { email: string; name: string | null } | null | undefined,
) {
  return !value
    ? "отправитель не указан"
    : value.name
      ? `${value.name} <${value.email}>`
      : value.email;
}

/**
 * Первый экран карточки: что в документе и какие файлы к нему приложены.
 *
 * Описание и последнее письмо читаются здесь же, без перехода по вкладкам;
 * полный разбор переписки и редактирование реквизитов остаются на своих вкладках.
 * Файлы приходят готовым блоком — тем же, что на вкладке «Файлы», чтобы открыть,
 * скачать и подписать файл можно было с первого экрана.
 */
export function DocumentOverview({
  document: doc,
  services,
  canViewFiles,
  canUpdate,
  onOpenTab,
}: {
  document: DocumentWorkflowDocument;
  services: ShellServices;
  canViewFiles: boolean;
  canUpdate: boolean;
  onOpenTab: (tab: "details" | "mail" | "files") => void;
}) {
  const mail = useQuery({
    queryKey: ["document-workflow", "documents", doc.id, "overview-letter"],
    queryFn: async () => {
      const list = await services.documentWorkflow.messages(doc.id);
      const time = (m: {
        received_at?: string | null;
        sent_at?: string | null;
      }) => new Date(m.received_at ?? m.sent_at ?? 0).getTime();
      const latest = [...list.data].sort((a, b) => time(b) - time(a))[0];

      return latest
        ? {
            total: list.data.length,
            message: await services.documentWorkflow.message(latest.id),
          }
        : null;
    },
    enabled: canViewFiles,
    retry: false,
  });
  const letter = mail.data?.message;
  const letterDate = formatDate(letter?.received_at ?? letter?.sent_at, true);
  const description = doc.description?.trim() ?? "";

  const facts: [string, string | null][] = [
    ["Вид", typeLabels[doc.type]],
    ["Приоритет", priorityLabels[doc.priority]],
    ["Контрагент", doc.counterparty?.name ?? null],
    ["Входящий номер", doc.incoming_number ?? null],
    ["Дата документа", formatDate(doc.document_date)],
    ["Получен", formatDate(doc.received_at, true)],
    [
      "Регистрация",
      doc.registration
        ? [doc.registration.number, formatDate(doc.registration.date)]
            .filter(Boolean)
            .join(" от ")
        : null,
    ],
    ["Листов", doc.sheets_count ? String(doc.sheets_count) : null],
    ["Экземпляров", doc.copies_count == null ? null : String(doc.copies_count)],
  ];

  return (
    <div className="dw-overview">
      <section className="dw-overview__content" aria-labelledby="dw-ov-content">
        <h3 id="dw-ov-content">Содержание</h3>
        {description ? (
          <p className="dw-overview__text">{description}</p>
        ) : (
          <p className="dw-secondary">
            Описание не заполнено.{" "}
            {canUpdate ? (
              <button
                type="button"
                className="button button--text button--compact"
                onClick={() => onOpenTab("details")}
              >
                Заполнить
              </button>
            ) : null}
          </p>
        )}
      </section>
      <aside className="dw-overview__facts" aria-labelledby="dw-ov-facts">
        <h3 id="dw-ov-facts">Реквизиты</h3>
        <dl>
          {facts
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
        </dl>
        {canUpdate ? (
          <button
            type="button"
            className="button button--text button--compact"
            onClick={() => onOpenTab("details")}
          >
            Изменить реквизиты
          </button>
        ) : null}
      </aside>
      <div className="dw-overview__files">
        {canViewFiles ? (
          <>
            <MailAttachments documentId={doc.id} services={services} />
            <button
              type="button"
              className="button button--text button--compact"
              onClick={() => onOpenTab("files")}
            >
              Файлы документа и подписи
            </button>
          </>
        ) : (
          <p className="dw-secondary">Файлы недоступны: недостаточно прав.</p>
        )}
      </div>
      {letter ? (
        <section className="dw-overview__letter" aria-labelledby="dw-ov-letter">
          <h3 id="dw-ov-letter">Последнее письмо</h3>
          <header>
            <strong>{letter.subject || "Без темы"}</strong>
            <span>
              {sender(letter.from)}
              {letterDate ? ` · ${letterDate}` : ""}
            </span>
          </header>
          <p className="dw-overview__text dw-overview__text--clamp">
            {letter.text_body?.trim() ||
              "Письмо без текста или в формате HTML — откройте его в переписке."}
          </p>
          <button
            type="button"
            className="button button--text button--compact"
            onClick={() => onOpenTab("mail")}
          >
            {mail.data && mail.data.total > 1
              ? `Вся переписка (${mail.data.total})`
              : "Открыть письмо"}
          </button>
        </section>
      ) : null}
    </div>
  );
}
